import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';


export const staffKeys = {
  all: ['staff'] as const,
  lists: () => [...staffKeys.all, 'list'] as const,
  roles: () => ['roles'] as const,
};

export interface StaffMember {
  id: string;
  name: string;
  email: string;
  phoneNumber: string | null;
  roleId: string;
  roleName: string;
  companyWide?: boolean;
  orgUnitId: string;
  orgUnitName: string;
  status: 'active' | 'pending_password_reset' | 'pending_activation' | 'suspended';
}

export interface Role {
  id: string;
  name: string;
  description: string | null;
}

export interface CreateStaffPayload {
  name: string;
  email: string;
  phoneNumber?: string;
  roleId: string;
  orgUnitId: string;
  companyWide?: boolean;
}

export function useStaffList() {
  return useQuery({
    queryKey: staffKeys.lists(),
    queryFn: async () => {
      return fetcher.get<StaffMember[]>('/staff/v1/admin/staff');
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: staffKeys.roles(),
    queryFn: async () => {
      const response = await fetcher.get<{ data: Role[]; meta: unknown }>('/staff/v1/rbac/roles');
      return response.data;
    },
  });
}

export function useCreateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateStaffPayload) =>
      fetcher.post<{ id: string; tempPassword?: string }>('/staff/v1/admin/staff', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
    },
  });
}

export function useUpdateStaffRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId, companyWide }: { userId: string; roleId: string; companyWide?: boolean }) =>
      fetcher.patch<unknown>(`/staff/v1/admin/staff/${userId}/role`, { roleId, companyWide }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
    },
  });
}

export function useUpdateStaffBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, orgUnitId }: { userId: string; orgUnitId: string }) =>
      fetcher.patch<unknown>(`/staff/v1/admin/staff/${userId}/branch`, { orgUnitId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
    },
  });
}

export function useResetStaffPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      fetcher.post<{ success: boolean; tempPassword?: string }>(`/staff/v1/admin/staff/${userId}/reset-password`, {}),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...staffKeys.all, "detail", userId] });
    },
  });
}

export function useStaffDetail(id: string) {
  return useQuery({
    queryKey: [...staffKeys.all, "detail", id] as const,
    queryFn: async () => {
      return fetcher.get<StaffMember>(`/staff/v1/admin/staff/${id}`);
    },
    enabled: !!id,
  });
}

export function useUpdateStaffStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: "active" | "suspended" }) =>
      fetcher.patch<unknown>(`/staff/v1/admin/staff/${userId}/status`, { status }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...staffKeys.all, "detail", variables.userId] });
    },
  });
}

export function useDeleteStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      fetcher.delete<unknown>(`/staff/v1/admin/staff/${userId}`),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: staffKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...staffKeys.all, "detail", userId] });
    },
  });
}

export interface StaffSession {
  id: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export function useStaffSessions(userId: string) {
  return useQuery({
    queryKey: [...staffKeys.all, 'sessions', userId] as const,
    queryFn: () => fetcher.get<StaffSession[]>(`/staff/v1/admin/sessions/${userId}`),
    enabled: !!userId,
  });
}

export function useRevokeStaffSession(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) =>
      fetcher.delete<unknown>(`/staff/v1/admin/sessions/${sessionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...staffKeys.all, 'sessions', userId] });
    },
  });
}

